/**
 * DashboardStats.tsx - Dashboard Key Metrics Display Component
 * 
 * This component displays key fundraising metrics and statistics in a
 * visually appealing grid layout with icons and formatted numbers.
 * 
 * Key Features:
 * - Key performance indicators (KPIs) display
 * - Formatted currency and number display
 * - Icon-based visual representation
 * - Responsive grid layout
 * - Real-time data updates from store
 * - Hover effects and animations
 * 
 * Metrics Displayed:
 * - Total Raised All Time: Aggregate fundraising amount
 * - Total Raised This Month: Current month performance
 * - Active Fundraisers: Number of currently active fundraisers
 * - Total Donors: Unique donor count across all fundraisers
 * 
 * Props:
 * - totalRaisedAllTime: number - Total amount raised across all fundraisers
 * - totalRaisedThisMonth: number - Amount raised in current month
 * - totalFundraisers: number - Count of active fundraisers
 * - totalDonors: number - Count of unique donors
 * 
 * Dependencies: Styled-components, Lucide icons, number formatting
 */

import React from 'react';
import styled from 'styled-components';
import { DollarSign, TrendingUp, Target, Users } from 'lucide-react';
import { FundRaiserDashboardModel } from '../../models';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 32px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f5f9;
  transition: transform 0.2s ease;
  opacity: 0;
  animation: statCardFadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  
  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }
  &:nth-child(4) { animation-delay: 0.4s; }
  
  @keyframes statCardFadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &:hover {
    transform: translateY(-2px);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const StatIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
`;

const StatTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 8px;
`;

const StatChange = styled.div<{ positive: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.positive ? '#10b981' : '#ef4444'};
`;

interface DashboardStatsProps {
  stats: FundRaiserDashboardModel
}

export const DashboardStats: React.FC<DashboardStatsProps> = ({
  stats
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };
  function calculatePercentageChange(
    current: number,
    previous: number,
    suffix: string
  ): { isPositive: boolean; formatted: string } {
    if (previous === 0) {
      if (current === 0) {
        return { isPositive: false, formatted: `` };
      } else {
        return { isPositive: true, formatted: `100% ${suffix}` };
      }
    }

    const change: number = current - previous;
    const percentage: number = (change / previous) * 100;
    const isPositive: boolean = percentage >= 0;
    const sign: string = isPositive ? "+" : "";
    const formattedPercentage = formatPercentage(Math.abs(percentage))
    return {
      isPositive,
      formatted: `${sign}${formattedPercentage}% ${suffix}`,

    };
  }
  function formatPercentage(value: number): string {
    const fixed = value.toFixed(2);
    return fixed.endsWith(".00") ? value.toFixed(0) : fixed;
  }


  const totalRaisedChange = calculatePercentageChange(stats.totalRaisedThisMonth, stats.totalRaisedLastMonth, 'from last month')
  const totalDonorsChange = calculatePercentageChange(stats.totalDonorsThisMonth, stats.totalDonorsLastMonth, 'growth')
  const thisMonthChange = calculatePercentageChange(stats.totalRaisedThisMonth, stats.totalRaisedLastMonth, 'from last month')
  const activeFundRaisersChange = calculatePercentageChange(stats.activeFundRaisers, stats.activeFundRaisersLastMonth, 'new this month')
  return (
    <StatsGrid>
      <StatCard>
        <StatHeader>
          <StatIcon color="#3b82f6">
            <DollarSign size={24} />
          </StatIcon>
          <StatTitle>Total Raised</StatTitle>
        </StatHeader>
        <StatValue>{formatCurrency(stats.totalRaised)}</StatValue>
        <StatChange positive={totalRaisedChange.isPositive}>{totalRaisedChange.formatted}</StatChange>
      </StatCard>

      <StatCard>
        <StatHeader>
          <StatIcon color="#10b981">
            <TrendingUp size={24} />
          </StatIcon>
          <StatTitle>This Month</StatTitle>
        </StatHeader>
        <StatValue>{formatCurrency(stats.totalRaisedThisMonth)}</StatValue>
        <StatChange positive={thisMonthChange.isPositive}>{thisMonthChange.formatted}</StatChange>
      </StatCard>

      <StatCard>
        <StatHeader>
          <StatIcon color="#f59e0b">
            <Target size={24} />
          </StatIcon>
          <StatTitle>Active Fundraisers</StatTitle>
        </StatHeader>
        <StatValue>{stats.activeFundRaisers}</StatValue>
        <StatChange positive={activeFundRaisersChange.isPositive}>{activeFundRaisersChange.formatted}</StatChange>
      </StatCard>

      <StatCard>
        <StatHeader>
          <StatIcon color="#8b5cf6">
            <Users size={24} />
          </StatIcon>
          <StatTitle>Total Donors</StatTitle>
        </StatHeader>
        <StatValue>{stats.totalDonors}</StatValue>
        <StatChange positive={totalDonorsChange.isPositive}>{totalDonorsChange.formatted}</StatChange>
      </StatCard>
    </StatsGrid>
  );
};